import { auth } from '../../firebase';
import { reportGlobalError } from './globalErrorReporter';

export class ApiRequestError extends Error {
  status: number;
  url: string;
  method: string;

  constructor(message: string, options: { status: number; url: string; method: string }) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = options.status;
    this.url = options.url;
    this.method = options.method;
  }
}

export interface AuthenticatedFetchOptions extends RequestInit {
  suppressErrorStatuses?: number[];
}

function getRequestLabel(url: string, method: string): string {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(url, base);
    return `${method.toUpperCase()} ${parsed.pathname}`;
  } catch {
    return `${method.toUpperCase()} ${url}`;
  }
}

async function parseProxyErrorMessage(response: Response): Promise<string | null> {
  try {
    const ct = response.headers.get('content-type');
    if (!ct?.includes('application/json')) return null;
    const body: unknown = await response.clone().json();
    if (!body || typeof body !== 'object') return null;
    const o = body as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof o.message === 'string' && o.message.trim()) parts.push(o.message.trim());
    if (Array.isArray(o.details)) {
      const d = o.details.map((x) => (typeof x === 'string' ? x : String(x))).filter(Boolean);
      if (d.length > 0) parts.push(d.join('; '));
    }
    if (parts.length === 0 && typeof o.error === 'string' && o.error.trim()) parts.push(o.error.trim());
    return parts.length > 0 ? parts.join(' — ') : null;
  } catch {
    return null;
  }
}

export async function authenticatedFetch(
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const method = options.method || 'GET';
  const requestLabel = getRequestLabel(url, method);
  const { suppressErrorStatuses = [], ...fetchOptions } = options;
  const user = auth.currentUser;

  if (!user) {
    const message = 'User not authenticated. Please sign in first.';
    reportGlobalError({ source: 'api_error', message: `${requestLabel}: ${message}` });
    throw new ApiRequestError(message, { status: 401, url, method });
  }

  const userEmail = user.email?.trim();
  if (!userEmail) {
    const message = 'Authenticated user has no email.';
    reportGlobalError({ source: 'api_error', message: `${requestLabel}: ${message}` });
    throw new ApiRequestError(message, { status: 401, url, method });
  }

  const idToken = await user.getIdToken();
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${idToken}`);

  const existingActor = headers.get('x-finn-actor');
  if (!existingActor?.toLowerCase().includes(userEmail.toLowerCase())) {
    headers.set('x-finn-actor', userEmail);
  }

  if (!headers.has('x-spaces-page')) {
    const page = window.location.pathname.split('/').filter(Boolean)[0] || 'people';
    headers.set('x-spaces-page', `people-space/${page}`);
  }

  let response: Response;
  try {
    response = await fetch(url, { ...fetchOptions, headers });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    reportGlobalError({ source: 'api_error', message: `${requestLabel}: ${message}` });
    throw new ApiRequestError(message, { status: 0, url, method });
  }

  if (!response.ok) {
    if (suppressErrorStatuses.includes(response.status)) return response;
    const fromBody = await parseProxyErrorMessage(response);
    const message = fromBody ?? `Request failed with status ${response.status}`;
    reportGlobalError({
      source: 'api_error',
      message: `${requestLabel}: ${message}`,
      statusCode: response.status,
    });
    throw new ApiRequestError(message, { status: response.status, url, method });
  }

  return response;
}

export async function getFirebaseIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}
